/**
 * Signature geometry (pure). The signature is kept as strokes in normalized 0..1 coordinates of a
 * box with a FIXED aspect ratio, so the same strokes can be drawn in any size (inline box, full
 * screen, exported PNG) and always keep the same shape.
 */

export interface SignaturePoint {
  x: number; // 0..1 across the box
  y: number; // 0..1 down the box
}
export type SignatureStroke = SignaturePoint[];

/** Width / height of the signing box (inline, full screen and exported image) */
export const SIGNATURE_ASPECT = 3 / 2;

/** Pixel width of the exported PNG (height follows the aspect ratio) */
export const SIGNATURE_EXPORT_WIDTH = 1200;

/** Largest box with the signature aspect that fits in the available area (height may be Infinity) */
export function fitBox(availW: number, availH: number, aspect = SIGNATURE_ASPECT): { w: number; h: number } {
  const w = Math.max(0, Math.min(availW, availH * aspect));
  return { w, h: w / aspect };
}

export function clamp01(n: number): number {
  return Math.min(1, Math.max(0, n));
}

/** Pen thickness proportional to the box width, so the signature looks the same at every size */
export function strokeWidthFor(boxWidth: number): number {
  return Math.max(1.5, boxWidth * 0.005);
}

/** Pointer position → normalized point inside the box */
export function toNormalized(clientX: number, clientY: number, rect: { left: number; top: number; width: number; height: number }): SignaturePoint {
  return {
    x: rect.width > 0 ? clamp01((clientX - rect.left) / rect.width) : 0,
    y: rect.height > 0 ? clamp01((clientY - rect.top) / rect.height) : 0,
  };
}

/** Draws the strokes scaled to a w × h box (smoothed with quadratic curves) */
export function drawStrokes(ctx: CanvasRenderingContext2D, strokes: SignatureStroke[], w: number, h: number): void {
  ctx.clearRect(0, 0, w, h);
  ctx.lineWidth = strokeWidthFor(w);
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.strokeStyle = '#111';
  ctx.fillStyle = '#111';

  for (const stroke of strokes) {
    if (stroke.length === 0) continue;
    if (stroke.length === 1) {
      ctx.beginPath();
      ctx.arc(stroke[0].x * w, stroke[0].y * h, ctx.lineWidth / 2, 0, Math.PI * 2);
      ctx.fill();
      continue;
    }
    ctx.beginPath();
    ctx.moveTo(stroke[0].x * w, stroke[0].y * h);
    for (let i = 1; i < stroke.length - 1; i++) {
      const midX = ((stroke[i].x + stroke[i + 1].x) / 2) * w;
      const midY = ((stroke[i].y + stroke[i + 1].y) / 2) * h;
      ctx.quadraticCurveTo(stroke[i].x * w, stroke[i].y * h, midX, midY);
    }
    const last = stroke[stroke.length - 1];
    ctx.lineTo(last.x * w, last.y * h);
    ctx.stroke();
  }
}

/** Transparent PNG of the strokes at the export size (null when nothing is drawn) */
export function renderStrokesToBlob(strokes: SignatureStroke[]): Promise<Blob | null> {
  if (strokes.length === 0) return Promise.resolve(null);
  const { w, h } = { w: SIGNATURE_EXPORT_WIDTH, h: Math.round(SIGNATURE_EXPORT_WIDTH / SIGNATURE_ASPECT) };
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) return Promise.resolve(null);
  drawStrokes(ctx, strokes, w, h);
  return new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
}
